import { useState } from 'react';
import { Palette, Type, Smile } from 'lucide-react';
import { House } from '../../data/houses';
import { useApp } from '../../contexts/AppContext';
import MediaChooser from '../../../components/common/MediaChooser';

interface HouseConfigPanelProps {
  house: House;
}

const emojiOptions = [
  '🦁', '🦅', '🐍', '🦡', '🔥', '⭐', '🌟', '💎',
  '🐺', '🦊', '🐻', '🦌', '🦄', '🐉', '🦋', '🌸',
  '⚡', '🌊', '🍀', '🌙', '☀️', '🎯', '🏆', '👑',
];

export default function HouseConfigPanel({ house }: HouseConfigPanelProps) {
  const { updateHouse, getHouse } = useApp();
  const currentHouse = getHouse(house.id) || house;

  const [name, setName] = useState(currentHouse.name);
  const [showColorPicker, setShowColorPicker] = useState<'color' | 'accent' | null>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const handleNameChange = (newName: string) => {
    setName(newName);
    updateHouse(house.id, { name: newName });
  };

  const handleAccentChange = (accent: string) => {
    updateHouse(house.id, { accent });
  };

  const handleIconChange = (icon: string) => {
    updateHouse(house.id, { icon });
    setShowEmojiPicker(false);
  };

  const generateGemGradient = (color: string) => {
    const lighten = (hex: string, percent: number) => {
      const num = parseInt(hex.replace('#', ''), 16);
      const amt = Math.round(2.55 * percent);
      const R = Math.min(255, (num >> 16) + amt);
      const G = Math.min(255, ((num >> 8) & 0x00ff) + amt);
      const B = Math.min(255, (num & 0x0000ff) + amt);
      return `#${((1 << 24) | (R << 16) | (G << 8) | B).toString(16).slice(1)}`;
    };
    return `linear-gradient(180deg, ${lighten(color, 40)} 0%, ${lighten(color, 10)} 50%, ${color} 100%)`;
  };

  const handlePrimaryColorChange = (color: string) => {
    updateHouse(house.id, {
      color,
      gemGradient: generateGemGradient(color),
    });
  };

  return (
    <div
      className="backdrop-blur-lg bg-white/10 rounded-2xl p-6 border-2 transition-all duration-300"
      style={{ borderColor: `${currentHouse.color}40` }}
    >
      <div className="flex items-center space-x-4 mb-6">
        <button
          onClick={() => setShowEmojiPicker(!showEmojiPicker)}
          className="text-5xl hover:scale-110 transition-transform cursor-pointer"
        >
          {currentHouse.icon}
        </button>
        <div className="flex-1">
          <input
            type="text"
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            className="w-full text-xl font-bold bg-transparent border-b-2 border-white/20 text-white focus:outline-none focus:border-white/50 pb-1"
          />
          <p className="text-white/60 text-sm mt-1">Click icon to change</p>
        </div>
      </div>

      {showEmojiPicker && (
        <div className="mb-6 p-4 bg-black/30 rounded-xl">
          <p className="text-white/60 text-sm mb-3 flex items-center">
            <Smile size={16} className="mr-2" /> Select Icon
          </p>
          <div className="grid grid-cols-8 gap-2">
            {emojiOptions.map(emoji => (
              <button
                key={emoji}
                onClick={() => handleIconChange(emoji)}
                className={`text-2xl p-2 rounded-lg hover:bg-white/10 transition-colors ${
                  currentHouse.icon === emoji ? 'bg-white/20 ring-2 ring-white/40' : ''
                }`}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-4">
        {/* Primary Color */}
        <div>
          <button
            onClick={() => setShowColorPicker(showColorPicker === 'color' ? null : 'color')}
            className="w-full flex items-center justify-between p-3 bg-white/5 hover:bg-white/10 rounded-xl transition-colors"
          >
            <div className="flex items-center space-x-3">
              <Palette size={20} className="text-white/60" />
              <span className="text-white">Primary Color</span>
            </div>
            <div
              className="w-8 h-8 rounded-lg border-2 border-white/20"
              style={{ backgroundColor: currentHouse.color }}
            />
          </button>

          {showColorPicker === 'color' && (
            <div className="mt-3 p-4 bg-black/30 rounded-xl">
              <div className="flex flex-col items-center gap-4">
                <input
                  type="color"
                  value={currentHouse.color}
                  onChange={(e) => handlePrimaryColorChange(e.target.value)}
                  className="h-16 w-full cursor-pointer rounded-lg border border-white/20 bg-transparent"
                  aria-label="Select primary color"
                />
              </div>
              <input
                type="text"
                value={currentHouse.color}
                onChange={(e) => handlePrimaryColorChange(e.target.value)}
                className="mt-3 w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white text-center uppercase focus:outline-none focus:border-white/40"
              />
            </div>
          )}
        </div>

        {/* Accent Color */}
        <div>
          <button
            onClick={() => setShowColorPicker(showColorPicker === 'accent' ? null : 'accent')}
            className="w-full flex items-center justify-between p-3 bg-white/5 hover:bg-white/10 rounded-xl transition-colors"
          >
            <div className="flex items-center space-x-3">
              <Type size={20} className="text-white/60" />
              <span className="text-white">Accent Color</span>
            </div>
            <div
              className="w-8 h-8 rounded-lg border-2 border-white/20"
              style={{ backgroundColor: currentHouse.accent }}
            />
          </button>

          {showColorPicker === 'accent' && (
            <div className="mt-3 p-4 bg-black/30 rounded-xl">
              <div className="flex flex-col items-center gap-4">
                <input
                  type="color"
                  value={currentHouse.accent}
                  onChange={(e) => handleAccentChange(e.target.value)}
                  className="h-16 w-full cursor-pointer rounded-lg border border-white/20 bg-transparent"
                  aria-label="Select accent color"
                />
              </div>
              <input
                type="text"
                value={currentHouse.accent}
                onChange={(e) => handleAccentChange(e.target.value)}
                className="mt-3 w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white text-center uppercase focus:outline-none focus:border-white/40"
              />
            </div>
          )}
        </div>

        {/* Background Image */}
        <div>
          <MediaChooser
            label="Background Image"
            value={currentHouse.backgroundImage || ''}
            onChange={(path) => updateHouse(house.id, { backgroundImage: path })}
            organizationId={house.id}
            imagesOnly={true}
            clearable={true}
            allowManualUrlEntry={true}
            placeholder="https://... or choose from media"
            buttonText="Choose"
          />
        </div>

        {/* Active/Inactive Toggle */}
        <div className="pt-4 border-t border-white/10">
          <label className="block text-white/60 text-sm mb-2">House Status</label>
          <button
            onClick={() => {
              const isCurrentlyDisabled = !!currentHouse.deactivatedOn;
              updateHouse(house.id, {
                deactivatedOn: isCurrentlyDisabled ? null : new Date().toISOString(),
              });
            }}
            className={`w-full px-4 py-3 rounded-xl text-white font-semibold transition-all ${
              currentHouse.deactivatedOn
                ? 'bg-red-600/80 hover:bg-red-700'
                : 'bg-green-600/80 hover:bg-green-700'
            }`}
          >
            {currentHouse.deactivatedOn ? '❌ Disabled' : '✅ Enabled'}
          </button>
        </div>
      </div>
    </div>
  );
}
